import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, CheckCircle, Radio, Zap } from 'lucide-react';
import { eventBus } from '@/map/core/events';

interface NotificationData {
  title: string;
  message: string;
  severity: 'critical' | 'warning' | 'info' | 'success';
}

export function ScenarioNotification() {
  const [notification, setNotification] = useState<NotificationData | null>(null);

  useEffect(() => {
    const unsub = eventBus.on('notification', (detail) => {
      setNotification({ title: detail.title, message: detail.message, severity: (detail.severity || 'info') as NotificationData['severity'] });
      setTimeout(() => setNotification(null), 5000);
    });
    return () => unsub();
  }, []);

  const getAccentColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#0ea5e9';
    }
  };

  const getTextColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400';
      case 'warning': return 'text-amber-400';
      case 'success': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const getBgGlow = (severity: string) => {
    switch (severity) {
      case 'critical': return 'shadow-[0_0_30px_rgba(239,68,68,0.15)]';
      case 'warning': return 'shadow-[0_0_30px_rgba(245,158,11,0.15)]';
      case 'success': return 'shadow-[0_0_30px_rgba(16,185,129,0.15)]';
      default: return 'shadow-[0_0_30px_rgba(14,165,233,0.15)]';
    }
  };

  const getIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return AlertTriangle;
      case 'warning': return Radio;
      case 'success': return CheckCircle;
      default: return Info;
    }
  };

  const getStatusLabel = (severity: string) => {
    switch (severity) {
      case 'critical': return 'CRITICAL ALERT';
      case 'warning': return 'WARNING';
      case 'success': return 'RESOLVED';
      default: return 'INFORMATION';
    }
  };

  return createPortal(
    <AnimatePresence>
      {notification && (
        <motion.div
          className="fixed top-8 left-1/2 z-[90] pointer-events-none"
          style={{ x: '-50%' }}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className={`bg-[var(--panel-bg)] backdrop-blur-2xl border border-[var(--border)] rounded-xl ${getBgGlow(notification.severity)} min-w-[450px] max-w-[550px] overflow-hidden`}>
            {/* Top accent bar */}
            <div className="h-[2px] w-full" style={{ background: getAccentColor(notification.severity) }} />

            <div className="p-4">
              {/* Header row */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${getAccentColor(notification.severity)}20` }}>
                  {(() => {
                    const Icon = getIcon(notification.severity);
                    return <Icon size={16} className={getTextColor(notification.severity)} />;
                  })()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold tracking-widest ${getTextColor(notification.severity)}`}>
                      {getStatusLabel(notification.severity)}
                    </span>
                    <Zap size={10} className={`${getTextColor(notification.severity)} animate-pulse`} />
                  </div>
                  <h3 className="text-[var(--text-primary)] font-semibold text-sm mt-0.5">{notification.title}</h3>
                </div>
                <div className="text-[10px] text-[var(--text-muted)] font-mono">
                  {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
              </div>

              {/* Message */}
              <p className="text-[12px] text-[var(--text-secondary)] mt-2.5 ml-11 leading-relaxed">{notification.message}</p>

              {/* Progress bar (auto-dismiss indicator) */}
              <div className="mt-3 ml-11 h-[2px] bg-[var(--badge-bg)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: getAccentColor(notification.severity) }}
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: 5, ease: 'linear' }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
